// ABOUTME: AI-powered message generation for SmartQuote using templates and smart text processing
// ABOUTME: Creates natural, personalized messages based on user input and detected trade categories

import { TradeCategory } from '@/lib/tradeCategories';
import { generateMessageTemplate } from '../../smartQuoteUtils';
import { debug } from '@/lib/utils';

export interface MessageGenerationOptions {
  userName?: string;
  userPostcode?: string;
  originalText: string;
  detectedCategory?: TradeCategory;
  urgency?: 'low' | 'medium' | 'high';
  preferredStyle?: 'formal' | 'casual' | 'friendly';
}

export interface GeneratedMessage {
  subject: string;
  body: string;
  confidence: number;
  reasoning: string;
}

/**
 * Generate a smart message based on user input and detected context
 */
export function generateSmartMessage(options: MessageGenerationOptions): GeneratedMessage {
  const {
    userName = '',
    userPostcode = '',
    originalText,
    detectedCategory,
    urgency = 'medium',
    preferredStyle = 'friendly'
  } = options;

  debug.log('Generating smart message with options:', options);

  // Extract key information from the original text
  const processedText = processUserText(originalText);

  // Generate subject line
  const subject = generateSubject(processedText, detectedCategory, urgency);

  // Generate message body using existing template system as base
  let body: string;
  let confidence = 0.7; // Base confidence
  let reasoning = 'Generated using template system';

  try {
    // Use existing template generation as starting point
    const templateMessage = generateMessageTemplate(
      processedText.cleanedText,
      userPostcode,
      userName,
      detectedCategory?.displayName
    );

    // Enhance the template with smart improvements
    body = enhanceMessage(templateMessage, processedText, detectedCategory, preferredStyle, urgency);

    // Calculate confidence based on how much information we have
    confidence = calculateConfidence(options, processedText, detectedCategory);
    reasoning = buildReasoning(options, processedText, detectedCategory);

  } catch (error) {
    debug.error('Error generating smart message:', error);

    // Fallback to basic template
    body = generateFallbackMessage(originalText, userPostcode, userName, detectedCategory?.displayName);
    confidence = 0.3;
    reasoning = 'Used fallback template due to generation error';
  }

  return {
    subject,
    body,
    confidence,
    reasoning
  };
}

/**
 * Process user text to extract useful information
 */
function processUserText(text: string): {
  cleanedText: string;
  urgencyIndicators: string[];
  timeIndicators: string[];
  problemType: string[];
  location: string[];
} {
  const lowerText = text.toLowerCase();

  // Extract urgency indicators
  const urgencyIndicators: string[] = [];
  const urgencyWords = ['urgent', 'asap', 'emergency', 'quickly', 'immediately', 'today', 'this week'];
  urgencyWords.forEach(word => {
    if (lowerText.includes(word)) urgencyIndicators.push(word);
  });

  // Extract time indicators
  const timeIndicators: string[] = [];
  const timeWords = ['tomorrow', 'next week', 'weekend', 'evening', 'morning', 'afternoon'];
  timeWords.forEach(word => {
    if (lowerText.includes(word)) timeIndicators.push(word);
  });

  // Extract problem type
  const problemType: string[] = [];
  const problemWords = ['broken', 'leaking', 'not working', 'stuck', 'blocked', 'install', 'replace', 'repair'];
  problemWords.forEach(word => {
    if (lowerText.includes(word)) problemType.push(word);
  });

  // Extract location indicators
  const location: string[] = [];
  const locationWords = ['kitchen', 'bathroom', 'garden', 'bedroom', 'living room', 'upstairs', 'downstairs'];
  locationWords.forEach(word => {
    if (lowerText.includes(word)) location.push(word);
  });

  return {
    cleanedText: text,
    urgencyIndicators,
    timeIndicators,
    problemType,
    location
  };
}

/**
 * Generate appropriate subject line
 */
function generateSubject(
  processedText: ReturnType<typeof processUserText>,
  category?: TradeCategory,
  urgency?: string
): string {
  let subject = 'Quote Request';

  if (category) {
    subject = `${category.displayName} Required`;
  }

  if (processedText.location.length > 0) {
    subject += ` - ${processedText.location[0]}`;
  }

  if (urgency === 'high' || processedText.urgencyIndicators.length > 0) {
    subject = `URGENT: ${subject}`;
  }

  return subject;
}

/**
 * Enhance the basic template message with smart improvements
 */
function enhanceMessage(
  baseMessage: string,
  processedText: ReturnType<typeof processUserText>,
  category?: TradeCategory,
  style: string = 'friendly',
  urgency: string = 'medium'
): string {
  let enhanced = baseMessage;

  // Add urgency if detected
  if (urgency === 'high' || processedText.urgencyIndicators.length > 0) {
    enhanced = enhanced.replace(
      'Are you available to discuss this?',
      'This is quite urgent - are you available to discuss this soon?'
    );
  }

  // Add timing if specified
  if (processedText.timeIndicators.length > 0) {
    const timing = processedText.timeIndicators[0];
    enhanced = enhanced.replace(
      'When would suit you?',
      `When would suit you? I was hoping for something ${timing} if possible.`
    );
  }

  // Adjust tone based on style
  if (style === 'formal') {
    enhanced = enhanced.replace('Hi there,', 'Dear Sir/Madam,');
    enhanced = enhanced.replace('Thanks,', 'Kind regards,');
  } else if (style === 'casual') {
    enhanced = enhanced.replace('Hi there,', 'Hi!');
    enhanced = enhanced.replace('Are you available to discuss this?', 'Let me know if you can help!');
  }

  return enhanced;
}

/**
 * Calculate confidence score based on available information
 */
function calculateConfidence(
  options: MessageGenerationOptions,
  processedText: ReturnType<typeof processUserText>,
  category?: TradeCategory
): number {
  let confidence = 0.3; // Base confidence

  // Add confidence for each piece of information we have
  if (options.userName) confidence += 0.1;
  if (options.userPostcode) confidence += 0.1;
  if (category) confidence += 0.2;
  if (options.originalText.length > 10) confidence += 0.1;
  if (processedText.problemType.length > 0) confidence += 0.1;
  if (processedText.location.length > 0) confidence += 0.1;

  return Math.min(confidence, 1.0);
}

/**
 * Build explanation of how the message was generated
 */
function buildReasoning(
  options: MessageGenerationOptions,
  processedText: ReturnType<typeof processUserText>,
  category?: TradeCategory
): string {
  const reasons: string[] = [];

  if (category) {
    reasons.push(`Detected you need a ${category.displayName.toLowerCase()}`);
  }

  if (processedText.urgencyIndicators.length > 0) {
    reasons.push('Added urgency based on your message');
  }

  if (processedText.timeIndicators.length > 0) {
    reasons.push('Included your preferred timing');
  }

  if (processedText.location.length > 0) {
    reasons.push(`Noted the location (${processedText.location[0]})`);
  }

  if (reasons.length === 0) {
    return 'Created a standard professional message';
  }

  return reasons.join(', ');
}

/**
 * Fallback message generation if template system fails
 */
function generateFallbackMessage(
  text: string,
  postcode: string,
  name: string,
  jobType?: string
): string {
  const postcodeText = postcode ? ` in ${postcode.toUpperCase()}` : '';
  const jobTypeText = jobType ? ` looking for ${jobType.toLowerCase()} services` : '';

  return `Hi,

I'm${jobTypeText}${postcodeText} and my neighbours recommended you.

${text}

Could you help with this? When would be a good time to discuss?

Thanks,
${name || '[Your name]'}`;
}
